import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBookings, useMentors } from '@/lib/queries'
import type { BookingFilters } from '@/lib/queries'
import { PageHeader, Card, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pagination } from '@/components/ui/Pagination'
import { BookingStatusPill, PaymentStatusPill } from '@/components/ui/Pill'
import {
  differsInTimezone,
  formatDateTime,
  formatDuration,
  formatInTimezone,
  formatMoney,
} from '@/lib/format'
import type { BookingAdmin } from '@/types/api'
import styles from './BookingsPage.module.css'

const STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PENDING_PAYMENT', label: 'Pending payment' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'NO_SHOW', label: 'No show' },
]

/**
 * A13 — Bookings.
 *
 * *** THERE IS NO MEETING LINK ON THIS SCREEN ***
 * Deliberate, and it is the whole free-tier access model: the Meet URL never
 * leaves the server, for anyone, including admins. Support answers "what time
 * is my session" from this table; the link is resolved by the app at join time.
 */
export function BookingsPage() {
  // The dashboard links here with `?status=PENDING_PAYMENT`, so the URL is the
  // source of truth for filters — a shared link then reproduces the same view.
  const [searchParams, setSearchParams] = useSearchParams()
  const mentors = useMentors()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const filters: BookingFilters = {
    page,
    page_size: pageSize,
    status: searchParams.get('status') ?? undefined,
    mentor_id: searchParams.get('mentor_id') ?? undefined,
    date_from: searchParams.get('date_from') ?? undefined,
    date_to: searchParams.get('date_to') ?? undefined,
    search: searchParams.get('search') ?? undefined,
  }

  const { data, isLoading, isFetching, error, refetch } = useBookings(filters)

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
    // Any filter change invalidates the current page number — page 4 of the old
    // result set is meaningless against the new one.
    setPage(1)
  }

  const hasFilters = ['status', 'mentor_id', 'date_from', 'date_to', 'search'].some(
    (key) => searchParams.get(key),
  )

  const columns: Array<Column<BookingAdmin>> = [
    {
      key: 'when',
      header: 'Session',
      render: (row) => (
        <div>
          <span className={styles.primary}>
            {formatDateTime(row.slot_start_utc)}
          </span>
          <p className={styles.secondary}>
            {formatDuration(row.duration_minutes)}
            {/*
              The mentor's local time, shown ONLY when it is genuinely a
              different clock time — see `differsInTimezone`. An admin in the
              mentor's own zone should not see the same time printed twice.
            */}
            {differsInTimezone(row.slot_start_utc, row.mentor_timezone) && (
              <>
                {' · '}
                {formatInTimezone(row.slot_start_utc, row.mentor_timezone)} for
                the mentor
              </>
            )}
          </p>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (row) => <span className={styles.primary}>{row.student_name}</span>,
    },
    {
      key: 'mentor',
      header: 'Mentor',
      render: (row) => (
        <div>
          <span className={styles.primary}>{row.mentor_name}</span>
          {row.category_name && (
            <p className={styles.secondary}>{row.category_name}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className={styles.statusCell}>
          <BookingStatusPill status={row.status} />
          {/*
            *** WHICH SIDE PULLED OUT IS WHAT AN ADMIN IS JUDGING ***
            "Cancelled" alone does not distinguish a student who changed their
            mind from a mentor who dropped a committed session — and those are
            different conversations. An admin is a third party to every booking,
            so there is no "you" here, only which side acted.
          */}
          {row.cancelled_by_role && (
            <span className={styles.secondary}>
              {row.cancelled_by_role === 'STUDENT'
                ? 'by student'
                : row.cancelled_by_role === 'MENTOR'
                  ? 'by mentor'
                  : 'by admin'}
            </span>
          )}
          {/*
            *** "USER_CANCELLED" IS A PLACEHOLDER, NOT A REASON ***
            No client sends a cancellation reason, so the server substitutes
            that literal — and rendering it produced "user cancelled" sitting
            under "by student", saying the same thing twice and less clearly.
            Only a REAL reason is worth a line of its own.
          */}
          {/*
            *** THE PLATFORM'S OWN FAILURE NEEDS TO STAND OUT ***
            A session that reached its start time with no video link could not
            happen, and somebody paid for it. Rendered as lowercase "no meeting
            link" beside ordinary churn, an admin scrolls past it.

            This is the one cancellation reason that asks the reader to DO
            something, so it gets a warning treatment and says what was already
            done automatically.
          */}
          {row.cancellation_reason === 'NO_MEETING_LINK' ? (
            <span className={styles.failedSession} title={
              row.subscription_billing
                ? 'The video link could not be created. The session has been returned to the student’s plan automatically.'
                : 'The video link could not be created. This session was paid for and did not happen — a refund may be due.'
            }>
              ⚠ Could not be held
              {row.subscription_billing
                ? ' · session returned'
                : ' · check refund'}
            </span>
          ) : (
            row.cancellation_reason &&
            row.cancellation_reason !== 'USER_CANCELLED' && (
              <span className={styles.secondary}>
                {row.cancellation_reason.replace(/_/g, ' ').toLowerCase()}
              </span>
            )
          )}
        </div>
      ),
    },
    {
      key: 'payment',
      header: 'Payment',
      secondary: true,
      render: (row) => <PaymentStatusPill status={row.payment_status} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <div>
          <span className={`${styles.primary} numeric`}>
            {/*
              *** A PLAN-COVERED SESSION HAS NO PRICE TO SHOW ***
              The figure is real, but nobody was charged it — the session came
              out of an allowance bought earlier. Printing it next to "No
              payment" makes an admin reconciling revenue hunt for money that
              was never owed.
            */}
            {row.subscription_billing
              ? 'Covered by plan'
              : formatMoney(row.total_amount, row.currency)}
          </span>
          {row.refund_amount && (
            <p className={`${styles.refund} numeric`}>
              −{formatMoney(row.refund_amount, row.currency)} refunded
            </p>
          )}
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Bookings"
        description="Every session booked in your organisation."
      />

      <Card flush>
        <div className={styles.filters}>
          <input
            type="search"
            defaultValue={searchParams.get('search') ?? ''}
            onChange={(event) => setFilter('search', event.target.value)}
            placeholder="Search by student or mentor…"
            className={styles.search}
            aria-label="Search bookings"
          />

          <select
            value={searchParams.get('status') ?? ''}
            onChange={(event) => setFilter('status', event.target.value)}
            className={styles.select}
            aria-label="Filter by status"
          >
            {STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <select
            value={searchParams.get('mentor_id') ?? ''}
            onChange={(event) => setFilter('mentor_id', event.target.value)}
            className={styles.select}
            aria-label="Filter by mentor"
          >
            <option value="">Any mentor</option>
            {(mentors.data?.mentors ?? []).map((mentor) => (
              <option key={mentor.user_id} value={mentor.user_id}>
                {mentor.full_name}
              </option>
            ))}
          </select>

          <div className={styles.dateRange}>
            <input
              type="date"
              value={searchParams.get('date_from') ?? ''}
              onChange={(event) => setFilter('date_from', event.target.value)}
              className={styles.select}
              aria-label="Sessions from"
            />
            <span className={styles.dateSeparator}>to</span>
            <input
              type="date"
              value={searchParams.get('date_to') ?? ''}
              onChange={(event) => setFilter('date_to', event.target.value)}
              className={styles.select}
              aria-label="Sessions until"
            />
          </div>

          {hasFilters && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                setSearchParams(new URLSearchParams(), { replace: true })
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </div>

        <Table
          columns={columns}
          rows={data?.bookings ?? []}
          rowKey={(row) => row.booking_id}
          loading={isLoading || isFetching}
          error={error}
          onRetry={() => void refetch()}
          isFiltered={hasFilters}
          emptyMessage="No bookings yet."
          emptyFilteredMessage="No bookings match these filters."
        />
      </Card>

      {data?.page && (
        <Pagination
          page={data.page}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}
    </PageStack>
  )
}
